import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Plus, X, Tags, Crown, Edit3 } from "lucide-react";
import { DatePicker } from "@/components/ui/date-picker";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useAuth } from "@/contexts/AuthContext";
import { ContentIdImportDialog } from "./ContentIdImportDialog";

interface ManageContentIdDialogProps {
  onDataAdded?: () => void;
}

export function ManageContentIdDialog({ onDataAdded }: ManageContentIdDialogProps) {
  const { toast } = useToast();
  const { hasFullAccess, hasBuAccess } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // Section 1: Adding Content ID
  const [contentId, setContentId] = useState("");
  const [ticketTime, setTicketTime] = useState<Date | undefined>(undefined);
  const [description, setDescription] = useState("");
  
  // Section 2: Update Content ID
  const [updateContentId, setUpdateContentId] = useState("");
  const [updateTicketTime, setUpdateTicketTime] = useState<Date | undefined>(undefined);
  const [updateDescription, setUpdateDescription] = useState("");
  const [selectedUpdateContentId, setSelectedUpdateContentId] = useState("");

  // Section 3: Managing Tags
  const [existingContentIds, setExistingContentIds] = useState<any[]>([]);
  const [selectedContentId, setSelectedContentId] = useState("");
  const [newTag, setNewTag] = useState("");
  const [tags, setTags] = useState<string[]>([]);

  // Fetch existing content IDs
  useEffect(() => {
    if (isOpen) {
      fetchExistingContentIds();
    }
  }, [isOpen]);

  const fetchExistingContentIds = async () => {
    try {
      const { data, error } = await supabase
        .from('topics_business_units')
        .select('*')
        .not('content_id', 'is', null)
        .order('content_id');
        
      if (error) throw error;
      setExistingContentIds(data || []);
    } catch (error) {
      console.error("Error fetching content IDs:", error);
    }
  };

  const handleAddContentId = async () => {
    if (!contentId.trim()) {
      toast({
        title: "Error",
        description: "Content ID is required",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('topics_business_units')
        .insert({
          content_id: contentId.trim(),
          ticket_time: ticketTime?.toISOString().split('T')[0] || null,
          description: description.trim() || null,
          type: 'Content',
          name: contentId.trim(),
          tags: []
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Content ID added successfully",
      });

      // Reset section 1 form
      setContentId("");
      setTicketTime(undefined);
      setDescription("");
      
      // Refresh data
      fetchExistingContentIds();
      if (onDataAdded) onDataAdded();
      
    } catch (error: any) {
      console.error("Error adding content ID:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to add content ID",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectUpdateContentId = (contentIdRecord: any) => {
    setSelectedUpdateContentId(contentIdRecord.id);
    setUpdateContentId(contentIdRecord.content_id || "");
    setUpdateTicketTime(contentIdRecord.ticket_time ? new Date(contentIdRecord.ticket_time) : undefined);
    setUpdateDescription(contentIdRecord.description || "");
  };

  const handleUpdateContentDetails = async () => {
    if (!selectedUpdateContentId) {
      toast({
        title: "Error",
        description: "Please select a Content ID to update",
        variant: "destructive"
      });
      return;
    }

    if (!updateContentId.trim()) {
      toast({
        title: "Error",
        description: "Content ID is required",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('topics_business_units')
        .update({
          content_id: updateContentId.trim(),
          ticket_time: updateTicketTime?.toISOString().split('T')[0] || null,
          description: updateDescription.trim() || null,
          name: updateContentId.trim()
        })
        .eq('id', selectedUpdateContentId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Content ID updated successfully",
      });

      fetchExistingContentIds();
      if (onDataAdded) onDataAdded();
      
    } catch (error: any) {
      console.error("Error updating content ID:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to update content ID",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectContentId = (contentIdRecord: any) => {
    setSelectedContentId(contentIdRecord.id);
    const existingTags = Array.isArray(contentIdRecord.tags) ? contentIdRecord.tags : [];
    setTags(existingTags);
  };

  const handleAddTag = () => {
    if (!newTag.trim()) return;
    
    const updatedTags = [...tags, newTag.trim()];
    setTags(updatedTags);
    setNewTag("");
  };

  const handleRemoveTag = (indexToRemove: number) => {
    const updatedTags = tags.filter((_, index) => index !== indexToRemove);
    setTags(updatedTags);
  };

  const handleUpdateTags = async () => {
    if (!selectedContentId) {
      toast({
        title: "Error",
        description: "Please select a Content ID first",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('topics_business_units')
        .update({ tags: tags })
        .eq('id', selectedContentId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Tags updated successfully",
      });

      fetchExistingContentIds();
      if (onDataAdded) onDataAdded();
      
    } catch (error: any) {
      console.error("Error updating tags:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to update tags",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setContentId("");
    setTicketTime(undefined);
    setDescription("");
    setSelectedContentId("");
    setNewTag("");
    setTags([]);
    setSelectedUpdateContentId("");
    setUpdateContentId("");
    setUpdateTicketTime(undefined);
    setUpdateDescription("");
  };

  // If BU Access user, don't render the dialog trigger
  if (hasBuAccess) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      setIsOpen(open);
      if (!open) resetForm();
    }}>
      <DialogTrigger asChild>
        <Button className="h-10 bg-blue-600 hover:bg-blue-700">
          <Plus className="h-4 w-4 mr-2" />
          Manage Content ID
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Manage Content ID</DialogTitle>
          <p className="text-sm text-muted-foreground mt-2">
            Comprehensive Content ID management system. Create new Content IDs and organize them with custom tags for efficient content tracking and categorization across all your content initiatives.
          </p>
        </DialogHeader>
        
        <Accordion type="single" collapsible className="w-full space-y-4">
          {/* Section 1: Adding Content ID - Only for Full Access Users */}
          <AccordionItem value="add-content-id" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <Plus className="h-5 w-5 text-blue-600" />
                  <span className="text-lg font-semibold">Add New Content ID</span>
                </div>
                {hasFullAccess && (
                  <Crown className="h-5 w-5 text-yellow-500" />
                )}
                {!hasFullAccess && (
                  <Badge variant="outline" className="ml-2 text-xs">
                    Admin Only
                  </Badge>
                )}
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-4">
              {!hasFullAccess ? (
                <div className="text-center py-8 bg-muted/50 rounded-lg">
                  <Crown className="h-12 w-12 text-yellow-500 mx-auto mb-3" />
                  <p className="text-muted-foreground font-medium">Full Access Required</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Only administrators can create new Content IDs
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="text-sm text-muted-foreground bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                    Create new Content IDs to organize and track your content. Each Content ID can have multiple tags for better categorization and management.
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="content-id" className="text-sm font-medium">Content ID *</Label>
                      <Input
                        id="content-id"
                        value={contentId}
                        onChange={(e) => setContentId(e.target.value)}
                        placeholder="Enter unique content ID"
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="ticket-time" className="text-sm font-medium">Ticket Time</Label>
                      <DatePicker
                        date={ticketTime}
                        setDate={setTicketTime}
                        placeholder="Select ticket time"
                        className="mt-1 w-full"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="description" className="text-sm font-medium">Description</Label>
                    <Textarea
                      id="description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Enter description for this content ID"
                      rows={3}
                      className="mt-1"
                    />
                  </div>
                  
                  <Button 
                    onClick={handleAddContentId} 
                    disabled={isLoading || !contentId.trim()}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    {isLoading ? "Adding..." : "Add Content ID"}
                  </Button>
                </div>
              )}
            </AccordionContent>
          </AccordionItem>

          {/* Section 2: Update Content ID - Only for Full Access Users */}
          <AccordionItem value="update-content-id" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <Edit3 className="h-5 w-5 text-purple-600" />
                  <span className="text-lg font-semibold">Update Content ID</span>
                </div>
                {hasFullAccess && (
                  <Crown className="h-5 w-5 text-yellow-500" />
                )}
                {!hasFullAccess && (
                  <Badge variant="outline" className="ml-2 text-xs">
                    Admin Only
                  </Badge>
                )}
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-4">
              {!hasFullAccess ? (
                <div className="text-center py-8 bg-muted/50 rounded-lg">
                  <Crown className="h-12 w-12 text-yellow-500 mx-auto mb-3" />
                  <p className="text-muted-foreground font-medium">Full Access Required</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Only administrators can update Content ID details
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="text-sm text-muted-foreground bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg">
                    Update existing Content ID information including name, ticket time, and description. Changes will be reflected across all related content and tags.
                  </div>
                  
                  <div>
                    <Label htmlFor="select-update-content-id" className="text-sm font-medium">Select Content ID to Update</Label>
                    <select
                      id="select-update-content-id"
                      value={selectedUpdateContentId}
                      onChange={(e) => {
                        const selected = existingContentIds.find(item => item.id === e.target.value);
                        if (selected) handleSelectUpdateContentId(selected);
                      }}
                      className="w-full p-3 mt-1 border border-gray-300 rounded-lg bg-background"
                    >
                      <option value="">Choose a Content ID to update</option>
                      {existingContentIds.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.content_id}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {selectedUpdateContentId && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="update-content-id" className="text-sm font-medium">Content ID *</Label>
                        <Input
                          id="update-content-id"
                          value={updateContentId}
                          onChange={(e) => setUpdateContentId(e.target.value)}
                          placeholder="Enter content ID"
                          className="mt-1"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="update-ticket-time" className="text-sm font-medium">Ticket Time</Label>
                        <DatePicker
                          date={updateTicketTime}
                          setDate={setUpdateTicketTime}
                          placeholder="Select ticket time"
                          className="mt-1 w-full"
                        />
                      </div>
                      
                      <div className="md:col-span-2">
                        <Label htmlFor="update-description" className="text-sm font-medium">Description</Label>
                        <Textarea
                          id="update-description"
                          value={updateDescription}
                          onChange={(e) => setUpdateDescription(e.target.value)}
                          placeholder="Enter description"
                          rows={3}
                          className="mt-1"
                        />
                      </div>
                      
                      <div className="md:col-span-2">
                        <Button 
                          onClick={handleUpdateContentDetails} 
                          disabled={isLoading || !updateContentId.trim()}
                          className="w-full bg-purple-600 hover:bg-purple-700"
                        >
                          {isLoading ? "Updating..." : "Update Content ID"}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </AccordionContent>
          </AccordionItem>

          {/* Section 3: Managing Tags */}
          <AccordionItem value="manage-tags" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <Tags className="h-5 w-5 text-green-600" />
                <span className="text-lg font-semibold">Manage Tags</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-4">
              <div className="space-y-4">
                <div className="text-sm text-muted-foreground bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                  Select a Content ID and manage its tags. Tags help categorize and filter your content for better organization and quick identification across different content types and campaigns.
                </div>
                
                <div>
                  <Label htmlFor="select-content-id" className="text-sm font-medium">Select Content ID</Label>
                  <select
                    id="select-content-id"
                    value={selectedContentId}
                    onChange={(e) => {
                      const selected = existingContentIds.find(item => item.id === e.target.value);
                      if (selected) handleSelectContentId(selected);
                    }}
                    className="w-full p-3 mt-1 border border-gray-300 rounded-lg bg-background"
                  >
                    <option value="">Choose a Content ID to manage tags</option>
                    {existingContentIds.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.content_id}
                      </option>
                    ))}
                  </select>
                </div>
                
                {selectedContentId && (
                  <div className="space-y-4">
                    <div className="flex gap-2">
                      <Input
                        value={newTag}
                        onChange={(e) => setNewTag(e.target.value)}
                        placeholder="Enter new tag"
                        className="flex-1"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddTag();
                          }
                        }}
                      />
                      <Button 
                        onClick={handleAddTag} 
                        disabled={!newTag.trim()}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        Add Tag
                      </Button>
                    </div>
                    
                    <div className="space-y-3">
                      <Label className="text-sm font-medium">Current Tags:</Label>
                      <div className="flex flex-wrap gap-2">
                        {tags.map((tag, index) => (
                          <Badge 
                            key={index} 
                            variant="secondary" 
                            className="flex items-center gap-2 px-4 py-2 text-sm font-medium"
                          >
                            {tag}
                            <X 
                              className="h-4 w-4 cursor-pointer hover:text-red-500 transition-colors" 
                              onClick={() => handleRemoveTag(index)}
                            />
                          </Badge>
                        ))}
                        {tags.length === 0 && (
                          <p className="text-muted-foreground text-sm italic">No tags assigned yet</p>
                        )}
                      </div>
                    </div>
                    
                    <Button 
                      onClick={handleUpdateTags} 
                      disabled={isLoading}
                      className="w-full bg-green-600 hover:bg-green-700"
                    >
                      {isLoading ? "Updating..." : "Update Tags"}
                    </Button>
                  </div>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </DialogContent>
    </Dialog>
  );
}
